import React from 'react';
import { cn, formatScoreChange } from '@/lib/utils';
import { Badge } from './badge';
import PlayerAvatar from '@/components/player/PlayerAvatar';
import { Tables } from '@/types/supabase';

export interface ProfileCardProps extends React.HTMLAttributes<HTMLDivElement> {
  name: string;
  handle: string;
  avatar?: string;
  avatarInitial?: string;
  player?: Tables<'player'>;
  joinedDate?: string;
  lastActivity?: string;
  children?: React.ReactNode;
  t?: (key: string) => string;
}

export const ProfileCard = React.forwardRef<HTMLDivElement, ProfileCardProps>(
  (
    {
      className,
      name,
      handle,
      avatar,
      avatarInitial: _avatarInitial,
      player,
      joinedDate,
      lastActivity,
      children,
      t,
      ...props
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn('border-border bg-card rounded-lg border p-6 shadow-sm', className)}
        {...props}
      >
        <div className="flex items-center space-x-4">
          <PlayerAvatar name={name} profilePhoto={avatar} size="lg" player={player} />
          <div className="flex-1">
            <h2 className="text-foreground text-xl font-bold">{name}</h2>
            <p className="text-muted">@{handle}</p>
            <div className="text-muted mt-2 space-y-1 text-sm">
              {joinedDate && (
                <div className="flex items-center space-x-2">
                  <span>👤</span>
                  <span>
                    {t ? t('joined') : 'Joined'} {joinedDate}
                  </span>
                </div>
              )}
              {lastActivity && (
                <div className="flex items-center space-x-2">
                  <span>🕐</span>
                  <span>
                    {t ? t('lastActive') : 'Last active'} {lastActivity}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
        {children}
      </div>
    );
  },
);
ProfileCard.displayName = 'ProfileCard';

export interface MatchResultBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  result: string;
  sets?: string[];
}

export const MatchResultBadge = React.forwardRef<HTMLSpanElement, MatchResultBadgeProps>(
  ({ className, result, sets, ...props }, ref) => {
    // Helper function to determine match result badge variant
    const getMatchResultVariant = (result: string): 'success' | 'destructive' | 'secondary' => {
      // Handle unplayed/scheduled matches with neutral styling
      // Check for dash (unplayed), empty string, or translated scheduled strings
      if (result === '-' || result === '') {
        return 'secondary';
      }

      // Parse score format like "3-0", "2-1", etc.
      const scoreParts = result.split('-');
      if (scoreParts.length === 2) {
        const myGames = parseInt(scoreParts[0], 10);
        const oppGames = parseInt(scoreParts[1], 10);
        if (!isNaN(myGames) && !isNaN(oppGames)) {
          return myGames > oppGames ? 'success' : 'destructive';
        }
      }
      // Fallback for non-score results
      return result.includes('W') || result.includes('won') ? 'success' : 'destructive';
    };

    return (
      <div className="block flex-col space-y-1">
        <Badge
          ref={ref}
          variant={getMatchResultVariant(result)}
          className={cn('text-[10px]', className)}
          {...props}
        >
          {result === '-' || result === '' ? '' : result}
        </Badge>
        {sets && <div className="text-muted text-xs">{sets.join(', ')}</div>}
      </div>
    );
  },
);
MatchResultBadge.displayName = 'MatchResultBadge';

export interface ScoreChangeProps extends React.HTMLAttributes<HTMLSpanElement> {
  value: string | number;
  isPositive: boolean;
}

export const ScoreChange = React.forwardRef<HTMLSpanElement, ScoreChangeProps>(
  ({ className, value, isPositive, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          'text-sm font-medium',
          isPositive ? 'text-green-600' : 'text-red-600',
          className,
        )}
        {...props}
      >
        {isPositive ? '+' : ''}
        {formatScoreChange(Number(value))}
      </span>
    );
  },
);
ScoreChange.displayName = 'ScoreChange';

export interface StatsGridProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  columns?: number;
}

export const StatsGrid = React.forwardRef<HTMLDivElement, StatsGridProps>(
  ({ className, children, columns = 2, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(`grid grid-cols-${columns} gap-4 md:grid-cols-${columns}`, className)}
        {...props}
      >
        {children}
      </div>
    );
  },
);
StatsGrid.displayName = 'StatsGrid';

export interface ContentGridProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const ContentGrid = React.forwardRef<HTMLDivElement, ContentGridProps>(
  ({ className, children, ...props }, ref) => {
    return (
      <div ref={ref} className={cn('grid grid-cols-1 gap-6 lg:grid-cols-2', className)} {...props}>
        {children}
      </div>
    );
  },
);
ContentGrid.displayName = 'ContentGrid';
